/**
 * OAuth 2.0 + PKCE Authentication Utilities
 *
 * Implements the PKCE (Proof Key for Code Exchange) flow for secure OAuth 2.0 authentication
 */

import { encrypt, decrypt, clearUserSeed } from './cryptoUtils';
import { sha256 } from 'js-sha256';
import {
  API_ROUTES,
  AUTH_CONSTANTS,
  ROUTES,
  getReactAppApiUrl,
  getReactAppAuthUrl,
} from './constants';
import { isOnline } from './fetchUtils';
import { devlogs } from './devlogs';

// Destructure constants for easier use
const {
  CODE_VERIFIER_KEY,
  STATE_KEY,
  RETURN_PATH_KEY,
  ACCESS_TOKEN_STORAGE_KEY,
  CLIENT_ID,
} = AUTH_CONSTANTS;

function normalizePathname(pathname: string): string {
  const p = pathname.replace(/\/+$/, '');
  return p === '' ? '/' : p;
}

/**
 * Browser is on the OAuth redirect route (handles trailing slash).
 * Used to avoid refresh-session / clearTokens while PKCE data may still be needed.
 */
export function isAuthCallbackLocation(): boolean {
  if (typeof window === 'undefined') return false;
  return normalizePathname(window.location.pathname) === ROUTES.AUTH_CALLBACK;
}

/**
 * OAuth redirect with ?code= (exchange not done). POST /refresh-session 401 runs clearTokens()
 * and wipes PKCE — must not run during this window.
 */
export function isOAuthCallbackWithAuthorizationCode(): boolean {
  if (!isAuthCallbackLocation()) return false;
  return Boolean(new URLSearchParams(window.location.search).get('code'));
}

// In-memory auth marker for cookie-backed Auth session.
let accessToken: string | null = null;
let tokenExpiry: number | null = null;
export type AccessTokenHydrationResult = 'valid' | 'expired' | 'missing';

// Guard to prevent concurrent refresh token calls (important: refresh tokens can't be reused)
let isRefreshing = false;
let refreshPromise: Promise<TokenData> | null = null;

/**
 * Snapshot PKCE values on first use during an OAuth redirect callback.
 * Survives React Strict Mode remounts and failed attempts that clear sessionStorage
 * so we do not retry the same URL with missing oauth_state / code_verifier.
 */
let oauthPkceSnapshot: {
  state: string | null;
  verifier: string | null;
} | null = null;

function takeOAuthPkceSnapshot(): {
  state: string | null;
  verifier: string | null;
} {
  if (!oauthPkceSnapshot) {
    oauthPkceSnapshot = {
      state: getState(),
      verifier: getCodeVerifier(),
    };
  }
  return oauthPkceSnapshot;
}

function clearOAuthPkceSnapshot(): void {
  oauthPkceSnapshot = null;
}

async function persistAccessToken(
  authMarker: string,
  expiresInSeconds: number,
): Promise<void> {
  try {
    const payload = JSON.stringify({
      marker: authMarker,
      expiresAt: Date.now() + expiresInSeconds * 1000,
    });
    const encryptedToken = await encrypt(payload);
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, encryptedToken);
  } catch (error) {
    console.error('Failed to persist auth session marker:', error);
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  }
}

function clearPersistedAccessToken(): void {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
}

export async function hydrateAccessTokenFromStorage(): Promise<AccessTokenHydrationResult> {
  const encryptedToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  if (!encryptedToken) {
    return 'missing';
  }

  try {
    const decryptedPayload = await decrypt(encryptedToken);
    const parsed = JSON.parse(decryptedPayload) as {
      marker?: string;
      expiresAt?: number;
    };
    if (!parsed.marker || typeof parsed.expiresAt !== 'number') {
      clearPersistedAccessToken();
      return 'missing';
    }
    if (Date.now() < parsed.expiresAt) {
      setAccessTokenInMemory(parsed.marker, parsed.expiresAt);
      return 'valid';
    }
    if (isOnline()) {
      clearPersistedAccessToken();
      accessToken = null;
      tokenExpiry = null;
      return 'expired';
    }
    setAccessTokenInMemory(parsed.marker, parsed.expiresAt);
    return 'valid';
  } catch (error) {
    console.error('Failed to hydrate access token from storage:', error);
    clearPersistedAccessToken();
    accessToken = null;
    tokenExpiry = null;
    return 'missing';
  }
}

const getRedirectUri = () => {
  if (typeof window !== 'undefined') {
    return (
      import.meta.env.VITE_REDIRECT_URI ||
      `${window.location.origin}/auth/callback`
    );
  }
  return import.meta.env.VITE_REDIRECT_URI || '/auth/callback';
};

/**
 * Generate a random string for code verifier
 * @param length - Length of the string (default: 43-128 chars as per PKCE spec)
 */
export function generateCodeVerifier(length: number = 128): string {
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let verifier = '';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  for (let i = 0; i < length; i++) {
    verifier += charset[array[i] % charset.length];
  }
  return verifier;
}

/**
 * Generate code challenge from code verifier using SHA256
 * @param verifier - The code verifier string
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const toBase64Url = (bytes: Uint8Array): string => {
    const base64 = btoa(String.fromCharCode(...Array.from(bytes)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  if (crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return toBase64Url(new Uint8Array(digest));
  }

  if (import.meta.env.PROD) {
    throw new Error(
      'PKCE requires Web Crypto (HTTPS or localhost). Open the app over HTTPS or use localhost.',
    );
  }

  // Dev-only: no subtle (e.g. HTTP + LAN IP) — same SHA-256 / base64url as RFC 7636.
  const hashBuffer: ArrayBuffer = sha256.arrayBuffer(verifier);
  const digest = new Uint8Array(hashBuffer);
  return toBase64Url(digest);
}

/**
 * Store code verifier in session storage
 */
export function storeCodeVerifier(verifier: string): void {
  sessionStorage.setItem(CODE_VERIFIER_KEY, verifier);
}

/**
 * Get code verifier from session storage
 */
export function getCodeVerifier(): string | null {
  return sessionStorage.getItem(CODE_VERIFIER_KEY);
}

/**
 * Clear code verifier from session storage
 */
export function clearCodeVerifier(): void {
  sessionStorage.removeItem(CODE_VERIFIER_KEY);
}

/**
 * Generate a random state string for CSRF protection
 */
export function generateState(): string {
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let state = '';
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  for (let i = 0; i < array.length; i++) {
    state += charset[array[i] % charset.length];
  }
  return state;
}

/**
 * Store state in session storage
 */
export function storeState(state: string): void {
  sessionStorage.setItem(STATE_KEY, state);
}

/**
 * Get state from session storage
 */
export function getState(): string | null {
  return sessionStorage.getItem(STATE_KEY);
}

/**
 * Clear state from session storage
 */
export function clearState(): void {
  sessionStorage.removeItem(STATE_KEY);
}

/**
 * Store return path in session storage
 */
export function storeReturnPath(path: string): void {
  sessionStorage.setItem(RETURN_PATH_KEY, path);
}

/**
 * Get return path from session storage
 */
export function getReturnPath(): string | null {
  return sessionStorage.getItem(RETURN_PATH_KEY);
}

/**
 * Clear return path from session storage
 */
export function clearReturnPath(): void {
  sessionStorage.removeItem(RETURN_PATH_KEY);
}

/**
 * Token storage interface
 */
export interface TokenData {
  accessToken: string;
  expiresIn: number;
}

/**
 * Set access token in memory (used by storeTokens and evaluateAccessTokenFromLocalStorage)
 */
function setAccessTokenInMemory(
  marker: string,
  expiresAt: number = Date.now() + 15 * 60 * 1000,
): void {
  accessToken = marker;
  tokenExpiry = expiresAt;
}

/**
 * Store Auth session marker + expiry locally.
 * The real access/refresh tokens stay in HttpOnly cookies / server storage.
 */
export async function storeTokens(tokens: TokenData): Promise<void> {
  await persistAccessToken(tokens.accessToken, tokens.expiresIn);
  setAccessTokenInMemory(
    tokens.accessToken,
    Date.now() + tokens.expiresIn * 1000,
  );
}

/**
 * Get access token from memory (not from localStorage)
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Deprecated in Auth mode. Refresh token never reaches frontend.
 */
export function getRefreshToken(): Promise<string | null> {
  return Promise.resolve(null);
}

/**
 * Check if access token is expired
 */
export function isTokenExpired(): boolean {
  if (!tokenExpiry) return true;
  return Date.now() >= tokenExpiry;
}

/**
 * Clear all local auth markers.
 */
export function clearTokens(): Promise<void> {
  // Clear access token from memory
  accessToken = null;
  tokenExpiry = null;
  clearPersistedAccessToken();

  // Clear user encryption seed used for encrypted local marker
  clearUserSeed();

  // During OAuth callback, a concurrent refresh-session 401 must not wipe PKCE session keys.
  if (!isOAuthCallbackWithAuthorizationCode()) {
    clearCodeVerifier();
    clearState();
    clearOAuthPkceSnapshot();
    clearReturnPath();
  }
  return Promise.resolve();
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return accessToken !== null && !isTokenExpired();
}

/**
 * Check whether we have any local Auth session marker.
 */
export async function hasLocalTokens(): Promise<boolean> {
  const result = await hydrateAccessTokenFromStorage();
  return result === 'valid' || isAuthenticated();
}

/**
 * Build authorization URL with PKCE parameters
 * Opens browser/WebView to authorization endpoint
 * If user is not logged in → Redirected to login page
 * After login → Redirected back to authorize endpoint
 * Authorization code generated → User redirected to redirect_uri?code=AUTH_CODE&state=STATE
 */
export async function buildAuthorizationUrl(): Promise<string> {
  clearOAuthPkceSnapshot();
  // Step 1: Generate PKCE challenge
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  storeCodeVerifier(verifier);

  // Step 2: Generate state for CSRF protection (optional but recommended)
  const state = generateState();
  storeState(state);

  const redirectUri = getRedirectUri();
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: redirectUri,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state: state,
  });

  // Build the authorization URL first
  const authUrl = `${getReactAppAuthUrl()}/auth/authorize?${params.toString()}`;

  // Add returnUrl parameter so auth server's login page knows where to redirect after login
  // The returnUrl should be the authorization URL itself (URL-encoded)
  const paramsWithReturnUrl = new URLSearchParams(params);
  paramsWithReturnUrl.append('returnUrl', encodeURIComponent(authUrl));

  const finalAuthUrl = `${getReactAppAuthUrl()}/auth/authorize?${paramsWithReturnUrl.toString()}`;
  return finalAuthUrl;
}

/**
 * Initiate login flow: build authorization URL and redirect to it
 */
export async function login(): Promise<void> {
  const authUrl = await buildAuthorizationUrl();
  window.location.href = authUrl;
}

/**
 * Exchange authorization code for tokens
 * @param code - Authorization code from redirect
 * @param returnedState - State parameter from redirect (for CSRF verification)
 */
export async function exchangeCodeForTokens(
  code: string,
  returnedState?: string,
): Promise<TokenData> {
  // Cannot complete OAuth callback without network (offline-first: show message, don't treat as auth failure)
  if (!isOnline()) {
    throw new Error(
      'Please connect to the internet to complete sign-in. You can try again when online.',
    );
  }

  const snap = takeOAuthPkceSnapshot();

  // Verify state matches (CSRF protection)
  if (returnedState) {
    const storedState = snap.state;
    if (!storedState) {
      clearCodeVerifier();
      clearState();
      clearOAuthPkceSnapshot();
      throw new Error(
        'OAuth state was lost before sign-in could finish. Close this tab and sign in again from the app.',
      );
    }
    if (returnedState !== storedState) {
      clearCodeVerifier();
      clearState();
      clearOAuthPkceSnapshot();
      throw new Error('State mismatch - possible CSRF attack');
    }
  }

  const verifier = snap.verifier;
  if (!verifier) {
    clearOAuthPkceSnapshot();
    throw new Error(
      'Code verifier not found. Please start the login process again.',
    );
  }

  const redirectUri = getRedirectUri();
  const exchangeUrl = `${getReactAppApiUrl()}${API_ROUTES.AUTH_SESSION.EXCHANGE}`;
  devlogs('[auth/exchange]', 'POST', exchangeUrl, {
    redirectUri,
    apiBaseUrl: getReactAppApiUrl() || '(empty)',
    credentials: 'include',
  });

  let response: Response;
  try {
    response = await fetch(exchangeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        code,
        code_verifier: verifier,
        redirect_uri: redirectUri,
        client_id: CLIENT_ID,
      }),
    });
  } catch (e) {
    devlogs('[auth/exchange]', 'fetch failed (network / CORS / blocked)', e);
    throw e;
  }

  devlogs('[auth/exchange]', 'response', {
    status: response.status,
    ok: response.ok,
    type: response.type,
  });

  if (!response.ok) {
    const error = (await response
      .json()
      .catch(() => ({ message: 'Failed to exchange code for tokens' }))) as {
      message?: string;
      detail?: string;
    };
    clearCodeVerifier();
    clearState();
    clearOAuthPkceSnapshot();
    throw new Error(
      error.message || error.detail || 'Failed to exchange code for tokens',
    );
  }

  const data = (await response.json()) as { expires_in?: number };

  // Clean up PKCE data
  clearCodeVerifier();
  clearState();
  clearOAuthPkceSnapshot();

  const tokenData: TokenData = {
    accessToken: 'auth',
    expiresIn: data.expires_in || 900,
  };

  await storeTokens(tokenData);
  devlogs('[auth/exchange]', 'exchange OK, local marker stored', {
    expiresIn: tokenData.expiresIn,
  });
  return tokenData;
}

/**
 * Hydrate auth marker from local storage without calling backend.
 */
async function hydrateTokenFromStorage(): Promise<TokenData | null> {
  const result = await hydrateAccessTokenFromStorage();
  if (result !== 'valid') return null;

  const token = getAccessToken();
  if (!token) return null;

  return {
    accessToken: token,
    expiresIn: 900,
  };
}

/** When offline: hydrate instead of calling backend. */
async function tryHydrateForOffline(): Promise<TokenData | null> {
  if (isOnline()) return null;
  return hydrateTokenFromStorage();
}

export async function refreshAccessToken(): Promise<TokenData> {
  // If already refreshing, return the existing promise
  // Must check BEFORE any await to prevent race: multiple callers would all pass
  // the guard and each trigger a refresh, causing "Token reuse detected".
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Set flag and create promise SYNCHRONOUSLY before any await
  // (await tryHydrateForOffline before this allowed concurrent callers to slip through)
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      // When offline, hydrate from storage instead of calling backend (offline-first)
      const offlineTokenData = await tryHydrateForOffline();
      if (offlineTokenData) return offlineTokenData;

      // No cookie session until code exchange finishes; refresh 401 would clearTokens() and PKCE.
      if (isOAuthCallbackWithAuthorizationCode()) {
        await hydrateAccessTokenFromStorage();
        if (isAuthenticated()) {
          const marker = getAccessToken();
          if (marker) {
            return { accessToken: marker, expiresIn: 900 };
          }
        }
        throw new Error('No active session');
      }

      let response: Response;
      try {
        response = await fetch(
          `${getReactAppApiUrl()}${API_ROUTES.AUTH_SESSION.REFRESH}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              accept: 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({}),
          },
        );
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // Network/fetch failed - try hydrate from storage (offline-first: don't throw, let user continue)
        const fallback = await hydrateTokenFromStorage();
        if (fallback) return fallback;
        if (error instanceof TypeError) {
          if (
            error.message === 'Failed to fetch' ||
            error.message.includes('fetch')
          ) {
            throw new Error('Network error: Unable to refresh token', {
              cause: error,
            });
          }
        }
        throw error;
      }

      if (!response.ok) {
        const error = (await response
          .json()
          .catch(() => ({ message: 'Failed to refresh session' }))) as {
          message?: string;
          detail?: string;
        };

        const errorMessage =
          error.message || error.detail || 'Failed to refresh session';
        if (response.status === 401) {
          await clearTokens();
          throw new Error('No active session');
        }
        throw new Error(`Session refresh failed: ${errorMessage}`);
      }

      const data = (await response.json()) as { expires_in?: number };

      const tokenData: TokenData = {
        accessToken: 'auth',
        expiresIn: data.expires_in || 900,
      };

      await storeTokens(tokenData);
      return tokenData;
    } finally {
      // Reset flags after completion (success or error)
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Logout Auth session and clear local markers.
 */
export async function revokeTokens(): Promise<void> {
  try {
    await fetch(`${getReactAppApiUrl()}${API_ROUTES.AUTH_SESSION.LOGOUT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({}),
    });
  } catch {
    // Even if revoke fails, clear local tokens
    // Error is ignored as we still want to clear local tokens
  } finally {
    await clearTokens();
  }
}
